import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MatDialog,
  MatDialogRef,
  MatDialogActions,
  MatDialogTitle,
  MatDialogContent,
  MatDialogModule,
  MAT_DIALOG_DATA
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';

export interface ConfirmationFinalizingInvoiceData {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: 'primary' | 'accent' | 'warn';
  cancelColor?: 'primary' | 'accent' | 'warn';
  invoiceDate?: Date;
  minDate?: Date;
  maxDate?: Date;
  showDatePicker?: boolean;
  pennylaneCountryLabel?: string;
}

export interface InvoiceFinalizationResult {
  confirmed: boolean;
  selectedDate?: Date;
}

@Component({
  selector: 'app-confirmation-finalizing-invoice',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatButtonModule,
    MatDialogActions,
    MatDialogTitle,
    MatDialogContent,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatNativeDateModule
  ],
  template: `
    <div class="dialog-container">
      <h5 mat-dialog-title class="dialog-title">{{ data.title }}</h5>
      
      <div mat-dialog-content>
        <div class="pennylane-current-target">
          <span class="pennylane-target-label">Pennylane target:</span>
          <span class="pennylane-target-value">Pennylane {{ data.pennylaneCountryLabel || 'Current workspace' }}</span>
        </div>

        <div class="message-card">
          <div [innerHTML]="data.message"></div>
        </div>

        <div class="date-picker-section" *ngIf="data.showDatePicker">
          <mat-form-field appearance="outline" class="w-100">
            <mat-label>Select a new date</mat-label>
            <input 
              matInput 
              [matDatepicker]="picker"
              [(ngModel)]="selectedDate"
              [min]="data.minDate"
              [max]="data.maxDate"
              (dateChange)="onDateChange($event)"
              required>
            <mat-datepicker-toggle matSuffix [for]="picker"></mat-datepicker-toggle>
            <mat-datepicker #picker></mat-datepicker>
          </mat-form-field>
          
          <div class="error-message" *ngIf="errorMessage">
            {{ errorMessage }}
          </div>
        </div>
      </div>
      
      <div mat-dialog-actions class="dialog-actions">
        <button 
          mat-flat-button 
          [color]="data.cancelColor || 'warn'"
          class="bg-error text-white"
          (click)="onCancel()">
          {{ data.cancelText || 'Cancel' }}
        </button>
        <button 
          mat-flat-button 
          [color]="data.confirmColor || 'primary'"
          class="bg-primary text-white"
          [disabled]="data.showDatePicker && !isValid"
          (click)="onConfirm()"
          cdkFocusInitial>
          {{ data.confirmText || 'Confirm' }}
        </button>
      </div>
    </div>
  `,
  styles: [`
    .dialog-container {
      padding: 24px;
      text-align: center;
      background: var(--bg-color, #ffffff);
      color: var(--text-color, #333333);
      min-width: 400px;
    }

    .dialog-title {
      font-size: 24px;
      font-weight: 600;
      margin-bottom: 16px;
      color: var(--text-color, #333333);
    }

    .message-card {
      background: var(--card-bg, #ffffff);
      border: 1px solid var(--border-color, #e0e0e0);
      border-radius: 8px;
      padding: 16px;
      margin-bottom: 20px;
      font-size: 14px;
      color: var(--text-secondary, #666666);
      text-align: left;
    }

    .pennylane-current-target {
      text-align: left;
      margin-bottom: 14px;
      display: flex;
      align-items: center;
      gap: 8px;
      flex-wrap: wrap;
    }

    .pennylane-target-label {
      font-size: 13px;
      color: var(--text-secondary, #666666);
      font-weight: 500;
    }

    .pennylane-target-value {
      display: inline-block;
      font-size: 13px;
      font-weight: 600;
      color: #5D87FF;
      background: rgba(93, 135, 255, 0.12);
      border: 1px solid rgba(93, 135, 255, 0.35);
      border-radius: 999px;
      padding: 4px 10px;
    }

    .message-card p {
      margin: 8px 0;
    }

    .date-picker-section {
      margin-top: 20px;
      text-align: left;
    }

    .w-100 {
      width: 100%;
    }

    ::ng-deep .mat-mdc-form-field {
      width: 100%;
    }

    ::ng-deep .mat-mdc-text-field-wrapper {
      background: var(--input-bg, #ffffff);
    }

    .error-message {
      color: #ff6692;
      font-size: 12px;
      margin-top: 8px;
      font-weight: 500;
    }

    .dialog-actions {
      display: flex;
      justify-content: center;
      gap: 12px;
      margin-top: 24px;
      padding: 0;
    }

    .bg-error {
      background-color: #ff6692 !important;
      color: white !important;
      border-radius: 8px;
      padding: 10px 24px;
      font-weight: 500;
      border: none;
      cursor: pointer;
      transition: background-color 0.3s ease;
    }

    .bg-error:hover {
      background-color: #ff6692 !important;
    }

    .bg-primary {
      background-color: #5D87FF !important;
      color: white !important;
      border-radius: 8px;
      padding: 10px 24px;
      font-weight: 500;
      border: none;
      cursor: pointer;
      transition: background-color 0.3s ease;
    }

    .bg-primary:hover {
      background-color: #4a6fd5 !important;
    }

    .bg-primary:disabled {
      background-color: #cccccc !important;
      cursor: not-allowed;
      opacity: 0.6;
    }

    /* Mode nuit */
    :host-context(.dark-theme) .dialog-container {
      --bg-color: #1a2537;
      --text-color: #ffffff;
      --card-bg: #1a2537;
      --border-color: #404040;
      --text-secondary: #cccccc;
      --input-bg: #1a2537;
    }

    :host-context(.dark-theme) .dialog-title {
      color: #ffffff;
    }

    :host-context(.dark-theme) .message-card {
      background: #1a2537;
      border-color: #404040;
      color: #cccccc;
    }

    :host-context(.dark-theme) ::ng-deep .mat-mdc-text-field-wrapper {
      background: #1a2537;
    }

    :host-context(.dark-theme) ::ng-deep .mat-mdc-form-field {
      color: #ffffff;
    }

    :host-context(.dark-theme) ::ng-deep .mat-mdc-input-element {
      color: #ffffff;
    }

    :host-context(.dark-theme) ::ng-deep .mdc-text-field--outlined .mdc-notched-outline__leading,
    :host-context(.dark-theme) ::ng-deep .mdc-text-field--outlined .mdc-notched-outline__notch,
    :host-context(.dark-theme) ::ng-deep .mdc-text-field--outlined .mdc-notched-outline__trailing {
      border-color: #404040;
    }

    :host-context(.dark-theme) ::ng-deep .mat-mdc-form-field-focus-overlay {
      background-color: transparent;
    }
  `]
})
export class ConfirmationFinalizingInvoiceComponent {
  selectedDate: Date | null = null;
  errorMessage: string = '';
  isValid: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<ConfirmationFinalizingInvoiceComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ConfirmationFinalizingInvoiceData
  ) {
    // Initialiser avec la date fournie ou la date min
    if (this.data.invoiceDate) {
      this.selectedDate = this.data.invoiceDate;
      this.isValid = true;
    } else if (this.data.minDate) {
      this.selectedDate = this.data.minDate;
      this.isValid = true;
    }
  }

  onDateChange(event: any): void {
    if (event.value) {
      this.selectedDate = event.value;
      this.validateDate();
    }
  }

  validateDate(): void {
    if (!this.selectedDate) {
      this.errorMessage = 'Please select a date';
      this.isValid = false;
      return;
    }

    if (this.data.minDate && this.selectedDate < this.data.minDate) {
      this.errorMessage = 'Date cannot be before the minimum date';
      this.isValid = false;
      return;
    }

    if (this.data.maxDate && this.selectedDate > this.data.maxDate) {
      this.errorMessage = 'Date cannot be after the maximum date';
      this.isValid = false;
      return;
    }

    this.errorMessage = '';
    this.isValid = true;
  }

  onConfirm(): void {
    if (this.data.showDatePicker) {
      this.validateDate();
      if (!this.isValid) {
        return;
      }
    }

    const result: InvoiceFinalizationResult = {
      confirmed: true,
      selectedDate: this.selectedDate || undefined
    };
    this.dialogRef.close(result);
  }

  onCancel(): void {
    const result: InvoiceFinalizationResult = {
      confirmed: false
    };
    this.dialogRef.close(result);
  }
}
